"use client";

import { AuthContext, CpfSignupProvider, useRuntimeConfig } from "@finbackoffice/site-core";
import { IModalForwardRefPropsV2, ModalsContextV2 } from "contexts";
import classNames from "classnames";
import dynamic from "next/dynamic";
import Link from "next/link";
import { FC, RefObject, useCallback, useContext, useRef } from "react";
import Button from "components/base/button/Button";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import Img from "components/base/img/Img";
import Modal from "components/base/modal/Modal";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import { ConfigContextV2 } from "contexts";
import { ModalTypes } from "utils/constants";
import PreloadComponent from "components/base/preload-component/PreloadComponent";
import { HeaderContext } from "components/header/Header";
import { AssetsDirectory } from "@finbackoffice/enums";
import WalletSelector from "../../wallet-selector/WalletSelector";
import SignupModal from "../../../account/signup/signup-modal/SignupModal";
import styles from "./default.module.sass";

const Settings = dynamic(() => import("./settings/Settings"), {
    loading: () => <PreloadComponent style={{ margin: 0, border: 0 }} />,
    ssr: false,
});

const Default: FC = () => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");

    const { siteDirection } = useContext(ConfigContextV2);
    const { currentModal, setCurrentModal, signupModalRef } = useContext(ModalsContextV2);
    const { isUserLoggedIn } = useContext(AuthContext);
    const settingsModalRef: RefObject<IModalForwardRefPropsV2 | null> = useRef(null);
    const { onWalletOpen, headerRef } = useContext(HeaderContext);

    const onSignupPress = useCallback(() => {
        if (currentModal === ModalTypes.SIGNUP) {
            setCurrentModal(null);
        } else {
            signupModalRef.current?.open();
        }
    }, [currentModal, setCurrentModal, signupModalRef]);

    const onSettingsButtonClick = useCallback(() => {
        if (settingsModalRef.current) {
            if (settingsModalRef.current.isOpened) {
                setCurrentModal(null);
            } else {
                settingsModalRef.current.open();
            }
        }
    }, [setCurrentModal]);

    return (
        <ErrorBoundary name={Default.name}>
            <header className={styles.header} ref={headerRef}>
                <Button
                    data-testid="settings-button"
                    type="button"
                    variant="custom"
                    className={`${styles.collapseBtn} ${
                        currentModal === ModalTypes.SETTINGS && styles.opened
                    }`}
                    onClick={onSettingsButtonClick}>
                    <span />
                    <span />
                    <span />
                </Button>
                <Link href="/">
                    <Img
                        source={`/${AssetsDirectory.Mobile}/logo.svg`}
                        alt={`${COMMON_SITE_CONFIGS.appName} logo`}
                        title={`${COMMON_SITE_CONFIGS.appName} logo`}
                        width={0}
                        height={0}
                        style={{ width: "100%", height: "auto" }}
                        prefixAppName
                    />
                </Link>

                <ErrorBoundary name={WalletSelector.name}>
                    <WalletSelector />
                </ErrorBoundary>
                {isUserLoggedIn ? (
                    <>
                        <Button
                            type="button"
                            onClick={onWalletOpen}
                            variant="custom"
                            className={styles.walletButton}
                            data-testid="open-wallet-button">
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/wallet.svg`}
                                className={classNames("svg-icon", styles.walletIcon)}
                            />
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            data-testid="signupButton"
                            type="button"
                            onClick={onSignupPress}
                            variant="primary"
                            className={styles.registerBtn}>
                            <Translate tid="header_registerNow" />
                        </Button>
                        <CpfSignupProvider>
                            <SignupModal />
                        </CpfSignupProvider>
                    </>
                )}
            </header>
            <ErrorBoundary name={Settings.name}>
                <Modal
                    ref={settingsModalRef}
                    type={ModalTypes.SETTINGS}
                    animateVariant={siteDirection === "rtl" ? "right" : "left"}>
                    <Settings />
                </Modal>
            </ErrorBoundary>
        </ErrorBoundary>
    );
};

export default Default;
